// src/components/maestros/forms/FormModalCreateCiudad.jsx
import React, { useRef, useState, useEffect } from 'react'
import { Loader } from '@googlemaps/js-api-loader'
import { MDBRow, MDBCol } from 'mdb-react-ui-kit'
import Swal from 'sweetalert2'
import axios from '../../../utils/axiosConfig'
import { registerOpenStreetMap, setOpenStreetMapView } from '../../../utils/openStreetMapType';

const FormModalCreateCiudad = ({ onSave, initialData }) => {
    const mapRef = useRef(null)
    const inputRef = useRef(null)
    const markerRef = useRef(null)

    const [map, setMap] = useState(null)
    const [relief, setRelief] = useState(false)
    const [mapType, setMapType] = useState('roadmap')

    const [nombreCiudad, setNombreCiudad] = useState('')
    const [lat, setLat] = useState('')
    const [lng, setLng] = useState('')

    const [agencias, setAgencias] = useState([])
    const [agencyId, setAgencyId] = useState('')

    // --- Cargar agencias
    useEffect(() => {
        axios.post(
            process.env.REACT_APP_SERVER + '/operaciones2/getagencias',
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                },
                withCredentials: true,
            }
        )
            .then(res => {
                if (res.data?.success) {
                    setAgencias(res.data.data.recordset || [])
                } else {
                    Swal.fire('Error', 'No se pudo cargar agencias', 'error')
                }
            })
            .catch(() => Swal.fire('Error', 'Falla de red al cargar lista', 'error'))
    }, [])

    // --- Mapa + Autocomplete + Listeners
    useEffect(() => {
        const loader = new Loader({
            apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || 'YOUR_API_KEY',
            version: 'weekly',
            libraries: ['places'],
        })

        let clickListener, dragendListener, placeListener
        let ac

        loader.load().then(() => {
            const google = window.google
            const defaultCenter = { lat: 4.6, lng: -74.1 }

            const m = new google.maps.Map(mapRef.current, {
                center: defaultCenter,
                zoom: 6,
                mapTypeId: mapType,
                mapTypeControl: false,
            })
            registerOpenStreetMap(google, m, { showControl: false })
            setMap(m)

            const marker = new google.maps.Marker({ map: m, draggable: true })
            markerRef.current = marker

            const updateCoords = (pos) => {
                const la = pos.lat()
                const lo = pos.lng()
                setLat(la.toFixed(6))
                setLng(lo.toFixed(6))
            }

            clickListener = m.addListener('click', (e) => {
                marker.setPosition(e.latLng)
                updateCoords(e.latLng)
            })

            dragendListener = marker.addListener('dragend', (e) => {
                updateCoords(e.latLng)
            })

            // Autocomplete (ciudades)
            ac = new google.maps.places.Autocomplete(inputRef.current, {
                types: ['(cities)'],
                fields: ['geometry', 'address_components', 'name', 'formatted_address'],
            })

            placeListener = ac.addListener('place_changed', () => {
                const place = ac.getPlace()
                if (!place?.geometry?.location) return

                // Nombre de ciudad
                let cityName = place.name || place.formatted_address || ''
                if (place.address_components) {
                    const comp = place.address_components
                    const loc = comp.find(c => c.types.includes('locality'))
                    const admin2 = comp.find(c => c.types.includes('administrative_area_level_2'))
                    const admin1 = comp.find(c => c.types.includes('administrative_area_level_1'))
                    cityName = (loc?.long_name || admin2?.long_name || cityName || admin1?.long_name || '')
                }
                setNombreCiudad(cityName)
                if (inputRef.current && inputRef.current.value !== cityName) {
                    inputRef.current.value = cityName
                }

                const p = place.geometry.location
                m.panTo(p)
                m.setZoom(12)
                marker.setPosition(p)
                updateCoords(p)
            })

            // initialData
            if (initialData?.latitud && initialData?.longitud) {
                const p = new google.maps.LatLng(
                    Number(initialData.latitud),
                    Number(initialData.longitud)
                )
                marker.setPosition(p)
                m.setCenter(p)
                m.setZoom(12)
                setLat(Number(initialData.latitud).toFixed(6))
                setLng(Number(initialData.longitud).toFixed(6))
            } else {
                const p = new google.maps.LatLng(defaultCenter.lat, defaultCenter.lng)
                marker.setPosition(p)
                setLat(defaultCenter.lat.toFixed(6))
                setLng(defaultCenter.lng.toFixed(6))
            }

            if (initialData?.nombreCiudad) {
                setNombreCiudad(initialData.nombreCiudad)
                if (inputRef.current) inputRef.current.value = initialData.nombreCiudad
            }
            if (initialData?.agenciaId) setAgencyId(initialData.agenciaId)
        })

        return () => {
            if (clickListener) clickListener.remove()
            if (dragendListener) dragendListener.remove()
            if (placeListener) placeListener.remove()
        }
    }, [initialData])

    // --- Tipo de mapa / relieve
    useEffect(() => {
        if (map) {
            if (relief) map.setMapTypeId('terrain')
            else setOpenStreetMapView(map, mapType)
        }
    }, [relief, mapType, map])

    // --- Buscar al presionar Enter (sin seleccionar sugerencia)
    const searchByQuery = () => {
        const google = window.google
        if (!map || !inputRef.current) return
        const q = inputRef.current.value?.trim()
        if (!q) return

        const service = new google.maps.places.PlacesService(map)
        service.textSearch({ query: q }, (results, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !results?.length) return
            const r = results[0]
            const p = r.geometry?.location
            if (!p) return

            map.panTo(p)
            map.setZoom(12)
            if (markerRef.current) markerRef.current.setPosition(p)
            setLat(p.lat().toFixed(6))
            setLng(p.lng().toFixed(6))
            setNombreCiudad(q) // o r.name / r.formatted_address si lo prefieres exacto
        })
    }

    // --- Guardar
    const submit = () => {
        if (!nombreCiudad.trim()) return Swal.fire('Error', 'Debe ingresar la ciudad', 'error')
        if (!lat || !lng) return Swal.fire('Error', 'Debe escoger una ubicación en el mapa', 'error')
        if (!agencyId) return Swal.fire('Error', 'Seleccione una agencia', 'error')

        onSave({
            nombreCiudad: nombreCiudad.trim(),
            latitud: lat,
            longitud: lng,
            agencyId,
        })
    }

    return (
        <div>
            <div style={{ position: 'relative', marginBottom: '1rem' }}>
                {/* Controles izquierda */}
                <div
                    style={{
                        position: 'absolute',
                        top: '8px',
                        left: '8px',
                        zIndex: 1,
                        background: 'rgba(255,255,255,0.9)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                    }}
                >
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="mapType"
                            checked={mapType === 'roadmap' && !relief}
                            onChange={() => { setRelief(false); setMapType('roadmap') }}
                            style={{ cursor: 'pointer' }}
                        />{' '}
                        Mapa
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="mapType"
                            checked={mapType === 'satellite' && !relief}
                            onChange={() => { setRelief(false); setMapType('satellite') }}
                            style={{ cursor: 'pointer' }}
                        />{' '}
                        Satélite
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="radio"
                            name="mapType"
                            checked={mapType === 'openstreetmap' && !relief}
                            onChange={() => { setRelief(false); setMapType('openstreetmap') }}
                            style={{ cursor: 'pointer' }}
                        />{' '}
                        OpenStreetMap
                    </label>
                    <label style={{ cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={relief}
                            onChange={(e) => setRelief(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />{' '}
                        Relieve
                    </label>
                </div>

                {/* Buscador centrado */}
                <input
                    id="search-box"
                    ref={inputRef}
                    type="text"
                    placeholder="Enter a location"
                    className="form-control"
                    defaultValue={initialData?.nombreCiudad || ''}
                    onChange={(e) => setNombreCiudad(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            searchByQuery()
                        }
                    }}
                    style={{
                        position: 'absolute',
                        top: '8px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '300px',
                        zIndex: 1,
                    }}
                />

                {/* Mapa */}
                <div ref={mapRef} style={{ width: '100%', height: '400px' }} />
            </div>

            {/* Latitud */}
            <MDBRow className="mb-3 g-0">
                <MDBCol size="12">
                    <label className="form-label">Latitud:</label>
                    <input type="text" className="form-control" value={lat} readOnly style={{ boxSizing: 'border-box' }} />
                </MDBCol>
            </MDBRow>

            {/* Longitud */}
            <MDBRow className="mb-3 g-0">
                <MDBCol size="12">
                    <label className="form-label">Longitud:</label>
                    <input type="text" className="form-control" value={lng} readOnly style={{ boxSizing: 'border-box' }} />

                </MDBCol>
            </MDBRow>

            {/* Agencias */}
            <MDBRow className="mb-3 g-0">
                <MDBCol size="12">
                    <label className="form-label">Agencias:</label>
                    <select
                        className="form-control"
                        value={agencyId}
                        onChange={(e) => setAgencyId(parseInt(e.target.value))}
                    >
                        <option value="">Selecciona una agencia</option>
                        {agencias.map((a) => (
                            <option key={a.IDAgencia} value={a.IDAgencia}>
                                {a.Descripcion}
                            </option>
                        ))}
                    </select>
                </MDBCol>
            </MDBRow>

            {/* Botón */}
            <div className="d-flex justify-content-end">
                <button className="btn-primary-modals-maestros" onClick={submit}>
                    Guardar
                </button>
            </div>
        </div>
    )
}

export default FormModalCreateCiudad
